// @angular
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnDestroy,
  HostListener,
  effect,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";
import { Router, RouterLink } from "@angular/router";
import { DomSanitizer } from "@angular/platform-browser";
// Translation service
import { TranslateService, TranslateModule } from "@ngx-translate/core";
// App services
import { EntityControllerService } from "../../../services/server/EntityController.service";
import {
  LanguageControllerService,
  LANGUAGE
} from "../../../services/language/LanguageController.service";
import { DataAnalysisControllerService } from "../../../services/server/DataAnalysisController.service";
import { ToastService } from "../../../services/shared/ToastService.service";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { trustImageDataUrl } from "../../../services/shared/html-sanitizer.util";
import { BrowserStorageLocalService } from "../../../services/shared/BrowserStorageServices/BrowserStorageLocalService.service";
import { AssociationControllerService } from "../../../services/server/AssociationController.service";
import { TokenCheckService } from "../../../services/shared/TokenCheckService.service";
import { DeviceRouteSelectorService } from "../../../services/shared/DeviceRouteSelectorService.service";
import { WeatherApiControllerService } from "../../../services/server/WeatherApiController.service";
import { AlarmControllerService } from "../../../services/server/AlarmController.service";
import { DateParserService } from "../../../services/shared/DateParserService.service";
import { NotificationControllerService } from "../../../services/server/NotificationController.service";
import { NotificationModalService } from "../../../modules/notification-module/notification-service/notification-modal.service";
import { UserControllerService } from "../../../services/server/UserController.service";
import { NavigationHelperService } from "../../../services/shared/NavigationHelperService.service";
import { ExternalConfigurationDeviceService } from "../../../services/shared/ExternalConfigurationDeviceService.service";
// Components
import { NavbarResponsiveComponent } from "../navbar-responsive/navbar-responsive.component";
import { NavbarTopSearchBoxComponent } from "./navbar-top-search-box/navbar-top-search-box.component";
// Interfaces
import { Client } from "../../../interfaces/ClientGlobalInterface.type";
import { Entity } from "../../../interfaces/EntityGlobalInterface.type";
import { Agrupation } from "../../../interfaces/AgrupationGlobalInterface.type";
import { CUPS_COLUMNS_INDEX, EntityDefinition } from "../../../interfaces/CupsGlobalInterface.type";
import { SessionData } from "../../../interfaces/SessionGlobalInterface.type";
import { CUSTOM_URLS } from "../../../screens/login/CUSTOM_URLS";
// Variables
import { PROFILES } from "../../../../assets/profiles/profiles";
import { UserConfig } from "../../../interfaces/UserGlobalInterface.type";
import { ConsumptionsCustomAlarmConfig } from "../../../screens/dashboard/alarms/AlarmInterface.type";
import { EntityTotalSnapshotComponent } from "./entity-total-snapshot/entity-total-snapshot.component";
import { NavbarUserDropdownComponent } from "./navbar-user-dropdown/navbar-user-dropdown.component";
import { MatButtonModule } from "@angular/material/button";
import { HelpDirective } from "../../../directives/help-directive/help.directive";
import { NavbarTopDropdownComponent } from "./navbar-top-dropdown/navbar-top-dropdown.component";
import { NgClass } from "@angular/common";
import { NavbarTopSearchNavigationComponent } from "./navbar-top-search-navigation/navbar-top-search-navigation.component";
import { TableDataColumn } from "../../table-module/TableInterface.type";

/**
 * Top navigation bar: client/entity/agrupation/CUPS selectors with search, session clock, weather,
 * pending notification count, logo and user dropdown.
 * Inputs: loadSession, profileStartUrl, hideDesktopMenus. Outputs: logoutFlag, logoImgUrlUpdate.
 */
@Component({
  selector: "app-navbar-top",
  templateUrl: "./navbar-top.component.html",
  styleUrls: ["./navbar-top.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    RouterLink,
    NavbarTopDropdownComponent,
    HelpDirective,
    NgClass,
    MatButtonModule,
    NavbarTopSearchBoxComponent,
    NavbarUserDropdownComponent,
    EntityTotalSnapshotComponent,
    NavbarResponsiveComponent,
    TranslateModule,
    NavbarTopSearchNavigationComponent
  ]
})
export class NavbarTopComponent implements OnInit, OnDestroy {
  /***************************************************************************/
  // ANCHOR Variables
  /***************************************************************************/

  // Session data stream (handled with effect)
  readonly PROFILES = PROFILES;

  // Session data
  sessionProfile: string;
  sessionUser: string;
  sessionUserName: { name: string; surname: string };
  sessionLanguage: string;
  languageName: string;
  sessionData: SessionData;
  currentEntityCupsConf: EntityDefinition[];
  currentCupsName: string;
  @Input() loadSession: boolean;
  @Input() profileStartUrl: string;

  // Client, entity and agrupation data
  userData: any;
  currentClient: Client;
  currentEntity: Entity;
  currentAgrupation: Agrupation;
  clientList: Client[] = [];
  clientListOriginal: Client[] = [];
  entityList: Entity[] = [];
  entityListOriginal: Entity[] = [];
  agrupationList: Agrupation[] = [];
  agrupationListOriginal: Agrupation[] = [];
  uniqueClient: boolean = false;
  updateSession: boolean;

  // Meter and CUPS lists
  meterList: any[] = [];
  meterListOriginal: any[] = [];
  cupsList: any[] = [];
  cupsListOriginal: any[] = [];
  searchLabel: string;

  // Pending notifications
  pendingNotifications: number = 0;

  // Clock and search box
  clockTime: { date: string; time: string; offset: string } = {
    date: null,
    time: null,
    offset: null
  };
  clockTimer: any;
  @ViewChild(NavbarResponsiveComponent)
  navbarResponsive: NavbarResponsiveComponent;
  @ViewChild(NavbarTopSearchBoxComponent)
  navbarTopSearchBox: NavbarTopSearchBoxComponent;

  // Logo
  logoImg: any;
  logoImgUrl: any;
  logoLoading: boolean = true;
  @Output() logoImgUrlUpdate = new EventEmitter<any>();

  // Logout
  @Output() logoutFlag = new EventEmitter<any>();

  // Responsive menu
  showResponsiveMenuFlag: boolean = false;
  @Input() hideDesktopMenus: boolean;
  customLogo = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText));
  navbarLogo: string = this.customLogo ? this.customLogo.img : "assets/img/logos/arsondatamini.ico";

  // Weather API
  weatherData: any;
  currentWeatherCoords: any;
  weatherLoading: boolean = false;
  weatherUpdateEnabled: boolean = false;
  weatherTypes: any = {
    thunderstorm: "fas fa-bolt",
    drizzle: "fas fa-cloud-rain",
    rain: "fas fa-cloud-showers-heavy",
    snow: "fas fa-snowflake",
    atmosphere: "fas fa-wind",
    clear: "fas fa-sun",
    clouds: "fas fa-cloud"
  };

  // Notifications
  notificationsRequestTimerMS: number = 600000;
  notificationsRequestTimer: any;

  // Help
  helpActive: boolean;

  @HostListener("document:keydown.escape", ["$event"]) onKeydownHandler(_event: KeyboardEvent) {
    if (this.helpActive) {
      this.toggleHelp();
    }
  }

  /***************************************************************************/
  // ANCHOR Constructor
  /***************************************************************************/

  constructor(
    private readonly DateParserService: DateParserService,
    private readonly DeviceRouteSelectorService: DeviceRouteSelectorService,
    private readonly domSanitizer: DomSanitizer,
    private readonly AlarmController: AlarmControllerService,
    private readonly EntityController: EntityControllerService,
    private readonly AssociationController: AssociationControllerService,
    readonly LanguageController: LanguageControllerService,
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly DataAnalysisController: DataAnalysisControllerService,
    private readonly NavigationHelperService: NavigationHelperService,
    private readonly NotificationController: NotificationControllerService,
    private readonly NotificationModalService: NotificationModalService,
    private readonly router: Router,
    readonly SessionDataService: SessionDataService,
    private readonly ToastService: ToastService,
    private readonly TokenCheckService: TokenCheckService,
    private readonly translate: TranslateService,
    private readonly UserController: UserControllerService,
    private readonly WeatherApiController: WeatherApiControllerService,
    private readonly ExternalConfigurationDeviceService: ExternalConfigurationDeviceService
  ) {
    // Profile
    effect(() => {
      const profile = this.SessionDataService.profile();
      if (profile) {
        this.sessionProfile = profile;
      }
    });
    // User
    effect(() => {
      const user = this.SessionDataService.user();
      if (user) {
        this.sessionUser = user;
        this.getUserName();
      }
    });
    // Agrupation
    effect(() => {
      const agrupation = this.SessionDataService.agrupation();
      if (untracked(() => this.SessionDataService.entity()) === null) {
        return;
      }
      if (this.currentAgrupation) {
        if (agrupation?.id !== this.currentAgrupation.id || this.updateSession) {
          this.checkAgrupationEntity(agrupation);
          this.setCurrentAgrupation(agrupation);
          this.weatherData = null;
          this.updateSessionData();
        }
      } else {
        this.checkAgrupationEntity(agrupation);
        this.setCurrentAgrupation(agrupation);
        this.updateSessionData();
      }
    });
    // Entity
    effect(() => {
      const entity = this.SessionDataService.entity();
      if (untracked(() => this.SessionDataService.client()) === null) {
        this.SessionDataService.clearGasDay();
        this.SessionDataService.clearGlobalManagement();
        this.SessionDataService.clearSharedGateways();
        this.SessionDataService.clearEntityExternalConfs();
        return;
      }
      if (this.currentEntity) {
        if (entity?.id !== this.currentEntity.id || this.updateSession) {
          this.setCurrentEntity(entity);
        } else if (
          this.sessionProfile != PROFILES.ARSON &&
          this.sessionProfile != PROFILES.USUARIO_ARSON
        ) {
          this.checkEntityFlags();
        }
      } else {
        this.setCurrentEntity(entity);
      }
    });
    // Client
    effect(() => {
      const client = this.SessionDataService.client();
      if (client == null) {
        this.SessionDataService.clearGasDay();
        this.SessionDataService.clearGlobalManagement();
        this.SessionDataService.clearSharedGateways();
        return;
      }
      if (this.currentClient) {
        if (client.clientId !== this.currentClient.clientId || this.updateSession) {
          this.setCurrentClient(client);
        } else if (
          this.sessionProfile === PROFILES.ARSON ||
          this.sessionProfile === PROFILES.USUARIO_ARSON
        ) {
          this.checkClientFlags();
        }
      } else {
        this.setCurrentClient(client);
      }
    });
    // Entity CUPS configuration
    effect(() => {
      if (this.SessionDataService.updateEntityCupsConfTick() > 0) {
        this.getEntityCupsConfiguration(this.currentEntity);
      }
    });
    // Language
    effect(() => {
      const language = this.SessionDataService.language();
      if (language) {
        this.sessionLanguage = language;
        this.languageSelector();
        this.updateSessionData();
      }
    });
    // Weather coordinates
    effect(() => {
      const weatherCoords = this.SessionDataService.weatherCoords();
      if (weatherCoords) {
        this.currentWeatherCoords = weatherCoords;
        this.weatherUpdateEnabled = true;
      }
    });
    // Nav update
    effect(() => {
      if (this.SessionDataService.updateNavTick() > 0) {
        this.updateSession = true;
        // Reset entity and agrupation so the effects run setCurrentEntity/Agrupation
        // when receiving the new signals (prevents the entity.id === currentEntity.id check from skipping them)
        this.currentEntity = null;
        this.currentAgrupation = null;
        this.clientList = [];
        this.clientListOriginal = [];
        this.entityList = [];
        this.entityListOriginal = [];
        this.agrupationList = [];
        this.agrupationListOriginal = [];
        if (untracked(() => this.SessionDataService.userLogged())) {
          this.loadData();
        }
      }
    });
    // Search box update
    effect(() => {
      if (this.SessionDataService.updateSearchBoxTick() > 0) {
        this.getSearchDropdown();
      }
    });
    // Notification loop
    effect(() => {
      const notificationLoop = this.SessionDataService.notificationLoop();
      if (notificationLoop === null) {
        return;
      }
      clearInterval(this.notificationsRequestTimer);
      if (notificationLoop) {
        this.notificationsRequestTimer = setInterval(
          () => this.getPendingNotifications(),
          this.notificationsRequestTimerMS
        );
      }
    });
  }

  /***************************************************************************/
  // ANCHOR Component initialization
  /***************************************************************************/

  ngOnInit(): void {
    // Load initial values
    this.sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    this.sessionProfile = untracked(() => this.SessionDataService.profile());
    this.sessionUser = untracked(() => this.SessionDataService.user());
    this.sessionLanguage = untracked(() => this.SessionDataService.language());
    this.languageSelector();
    // Number format
    this.SessionDataService.sendNumberFormat(
      this.sessionData?.numberFormat ??
        (this.sessionData.language === LANGUAGE.INGLES ? LANGUAGE.INGLES : LANGUAGE.ESPANOL)
    );
    // Date format
    this.SessionDataService.sendDateFormat(this.sessionData?.dateFormat ?? "DD/MM/YYYY");

    // Load data from server
    this.loadData();
    if (
      this.sessionProfile !== PROFILES.INSTALADOR &&
      this.sessionProfile !== PROFILES.ADMIN_INSTALLATION &&
      this.sessionProfile !== PROFILES.TELEMEDIDA
    ) {
      this.getPendingNotifications();
      clearInterval(this.notificationsRequestTimer);
      this.notificationsRequestTimer = setInterval(
        () => this.getPendingNotifications(),
        this.notificationsRequestTimerMS
      );
    }
  }

  /***************************************************************************/
  // ANCHOR Component destruction
  /***************************************************************************/

  ngOnDestroy(): void {
    clearInterval(this.clockTimer);
    clearInterval(this.notificationsRequestTimer);
    this.SessionDataService.clearAll();
  }

  /***************************************************************************/
  // ANCHOR Functions
  /***************************************************************************/

  // Get user name
  getUserName(): void {
    this.UserController.getUserName(this.sessionUser).subscribe(response => {
      if (response["code"] === 0) {
        this.sessionUserName = response["body"];
      }
    });
  }

  // Logo load notification
  onLogoLoad(): void {
    this.logoLoading = false;
  }

  // Show responsive menu
  showResponsiveMenu(): void {
    this.showResponsiveMenuFlag = !this.showResponsiveMenuFlag;
  }

  // Logout
  logout(): void {
    this.logoutFlag.emit();
  }

  // Relogin
  relogin(): void {
    this.ToastService.firePasswordInput().then((password: string) => {
      if (password) {
        this.TokenCheckService.renewSession(password);
      }
    });
  }

  // Toggle help
  toggleHelp(): void {
    this.helpActive = !untracked(() => this.SessionDataService.helpActive());
    this.SessionDataService.sendHelpActive(this.helpActive);
    this.helpActive
      ? document.body.classList.add("_help.active")
      : document.body.classList.remove("_help.active");
  }

  /***************************************************************************/
  // ANCHOR Session load and update
  /***************************************************************************/

  // Load client data
  loadData(): void {
    this.sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    if (!this.profileStartUrl) {
      this.profileStartUrl = this.sessionData.profileStartUrl;
    }
    let originalProfile = untracked(() => this.SessionDataService.originalProfile());
    this.EntityController.getEntityBy(
      originalProfile === PROFILES.ARSON ? PROFILES.ARSON : this.sessionProfile
    ).subscribe(response => {
      this.userData = response["body"];
      if (this.userData) {
        // Data load for ARSON profile
        if (
          this.sessionProfile === PROFILES.ARSON ||
          this.sessionProfile === PROFILES.USUARIO_ARSON ||
          originalProfile === PROFILES.ARSON ||
          originalProfile === PROFILES.USUARIO_ARSON
        ) {
          this.getClientList();
          this.SessionDataService.sendClientList(this.clientListOriginal);
          // Saved session load
          if (this.loadSession || this.updateSession) {
            // Current client update
            this.loadCurrentClient();
            // Current entity update
            this.loadCurrentEntity();
            // Current agrupation update
            this.loadCurrentAgrupation();
            // Session remaining time update
            this.TokenCheckService.sessionTimeRemaining();
            // Session load finished update
            this.updateSession = false;
          } else {
            this.SessionDataService.sendClient(this.clientListOriginal[0]);
          }
          // Data load for non-ARSON profile
        } else {
          this.uniqueClient = true;
          let client = {
            clientId: this.userData[0].client,
            clientName: null,
            entityList: this.userData,
            gas: this.userData[0].gasClient
          };
          // Client list update with the single client
          this.clientList = [client];
          this.clientListOriginal = [client];
          this.SessionDataService.sendClientList(this.clientListOriginal);
          // Saved session load
          if (this.loadSession || this.updateSession) {
            // Current client update
            this.loadCurrentClient();
            // Current entity update
            this.loadCurrentEntity();
            // Current agrupation update
            this.loadCurrentAgrupation();
            // Session remaining time update
            this.TokenCheckService.sessionTimeRemaining();
            // Session load finished update
            this.updateSession = false;
          } else {
            this.SessionDataService.sendClient(this.clientListOriginal[0]);
          }
        }
      }
    });
  }

  // Preload client
  loadCurrentClient(): void {
    let targetClient: Client;
    if (this.sessionData.current.client) {
      targetClient = this.clientListOriginal?.find((client: any) => {
        if (client.clientId) {
          return client.clientId === this.sessionData.current.client.clientId;
        }
        return false;
      });
    }
    if (!targetClient) {
      targetClient = this.clientList[0];
    }
    // Set currentClient synchronously so loadCurrentEntity can find it
    if (targetClient) {
      this.currentClient = targetClient;
      this.getEntityList();
    }
    this.SessionDataService.sendClient(targetClient);
  }

  // Preload entity
  loadCurrentEntity(): void {
    if (this.sessionData.current.entity) {
      let currentEntity = this.currentClient?.entityList?.find(
        (entity: any) => entity.id === this.sessionData.current.entity.id
      );
      if (currentEntity) {
        this.SessionDataService.sendEntity(currentEntity);
      } else {
        this.SessionDataService.sendEntity(this.currentClient?.entityList[0]);
      }
    } else {
      this.SessionDataService.sendEntity(this.currentClient?.entityList[0]);
    }
  }

  // Preload agrupation
  // Does not depend on this.currentEntity (set asynchronously by the effect)
  // but looks up the entity in the raw data of this.currentClient
  loadCurrentAgrupation(): void {
    const targetEntityId = this.sessionData?.current?.entity?.id;
    const entityData: Entity = targetEntityId
      ? this.currentClient?.entityList?.find((e: Entity) => e.id === targetEntityId) ||
        this.currentClient?.entityList?.[0]
      : this.currentClient?.entityList?.[0];
    const agrupations: Agrupation[] = entityData?.agrupations || [];

    let targetAgrupation: Agrupation;
    if (this.sessionData.current.agrupation) {
      targetAgrupation = agrupations.find(
        (a: Agrupation) => a.id === this.sessionData.current.agrupation?.id
      );
    }
    this.SessionDataService.sendAgrupation(targetAgrupation || agrupations[0]);
  }

  // Update session data in localStorage
  updateSessionData(): void {
    if (!untracked(() => this.SessionDataService.sessionLoadingFlag())) {
      let updatedSessionData: any = this.BrowserStorageLocalService.getJsonValue("session");
      updatedSessionData["current"] = {};
      updatedSessionData["current"]["client"] = untracked(() => this.SessionDataService.client());
      updatedSessionData["current"]["entity"] = untracked(() => this.SessionDataService.entity());
      updatedSessionData["current"]["agrupation"] = untracked(() =>
        this.SessionDataService.agrupation()
      );
      updatedSessionData["language"] = this.sessionLanguage;
      this.BrowserStorageLocalService.setJsonValue("session", updatedSessionData);
    }
  }

  /***************************************************************************/
  // ANCHOR Language
  /***************************************************************************/

  // Language selector
  languageSelector(): void {
    switch (this.sessionLanguage) {
      case LANGUAGE.INGLES:
        this.languageName = "English";
        break;
      case LANGUAGE.ESPANOL:
        this.languageName = "Castellano";
        break;
      case "ca":
        this.languageName = "Català";
        break;
      case LANGUAGE.CATALAN:
        this.languageName = "Català";
        break;
      case LANGUAGE.FRANCES:
        this.languageName = "Français";
        break;
      case LANGUAGE.ITALIANO:
        this.languageName = "Italiano";
        break;
      default:
        this.languageName = "English";
        break;
    }
  }

  // Change user language
  switchLanguage(newLanguage: string): void {
    // Update language in user profile
    this.LanguageController.changeLang(
      newLanguage === LANGUAGE.CATALAN ? "CA" : newLanguage.toUpperCase()
    ).subscribe(() => {
      this.SessionDataService.sendLanguage(newLanguage);
    });
  }

  // Change number format
  switchNumberFormat(format: string): void {
    this.SessionDataService.sendNumberFormat(format);
    this.sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    this.sessionData.numberFormat = format;
    this.BrowserStorageLocalService.setJsonValue("session", this.sessionData);
    this.SessionDataService.sendReloadPanelFlag();
  }

  // Change date format
  switchDateFormat(format: string): void {
    this.SessionDataService.sendDateFormat(format);
    this.sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    this.sessionData.dateFormat = format;
    this.BrowserStorageLocalService.setJsonValue("session", this.sessionData);
    this.SessionDataService.sendReloadPanelFlag();
  }

  /***************************************************************************/
  // ANCHOR List filtering
  /***************************************************************************/

  // Filter client, entity and agrupation lists
  dropdownFilter(inputData: string, dataType: string, cupsSelected?: boolean): void {
    switch (dataType) {
      case "client":
        this.clientList = this.filterArrayOfObjects(
          this.clientListOriginal,
          ["clientName"],
          inputData
        );
        break;
      case "entity":
        this.entityList = this.filterArrayOfObjects(
          this.entityListOriginal,
          ["entity", "alias"],
          inputData
        );
        break;
      case "agrupation":
        this.agrupationList = this.filterArrayOfObjects(
          this.agrupationListOriginal,
          ["name", "alias"],
          inputData
        );
        break;
      case "search":
        if (!cupsSelected) {
          this.meterList = this.filterArrayOfObjects(
            this.meterListOriginal,
            ["descripcion"],
            inputData
          );
        } else {
          this.cupsList = this.filterArrayOfObjects(
            this.cupsListOriginal,
            ["descripcion"],
            inputData
          );
        }
        break;
      default:
        break;
    }
  }

  // Filter dropdown array
  filterArray(array: any, inputData: string): void {
    return array.filter((arrayElement: any) => {
      if (arrayElement?.toUpperCase().includes(inputData.toUpperCase())) {
        return arrayElement;
      }
      return null;
    });
  }

  // Filter dropdown array of objects
  filterArrayOfObjects(array: any, attributes: string[], inputData: string): any[] {
    return array.filter((arrayElement: any) => {
      if (
        attributes.some(attribute =>
          arrayElement[attribute]?.toUpperCase().includes(inputData.toUpperCase())
        )
      ) {
        return arrayElement;
      }
      return null;
    });
  }

  // Reset search boxes and list arrays
  resetSearchBox(searchBox: any): void {
    switch (searchBox) {
      case "client":
        this.clientList = [...this.clientListOriginal];
        break;
      case "entity":
        this.entityList = [...this.entityListOriginal];
        break;
      case "agrupation":
        this.agrupationList = [...this.agrupationListOriginal];
        break;
      case "search":
        this.meterList = [...this.meterListOriginal];
        this.cupsList = [...this.cupsListOriginal];
        break;
      default:
        break;
    }
  }

  /***************************************************************************/
  // ANCHOR Get client, entity and agrupation lists
  /***************************************************************************/

  // Get entity logo
  getEntityImg(): void {
    if (this.sessionProfile === PROFILES.TELEMEDIDA) {
      let customLogo = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText));
      this.logoImgUrl = customLogo?.img ? customLogo?.img : "assets/img/logos/arsondata.png";
      this.logoImgUrlUpdate.emit(this.logoImgUrl);
    } else {
      this.EntityController.getImg(this.currentEntity.id).subscribe(response => {
        if (response["code"] === 0) {
          this.logoImg = response["body"];
          if (this.logoImg) {
            this.logoImgUrl =
              trustImageDataUrl(this.domSanitizer, this.logoImg) ?? "assets/img/logos/arsondata.png";
            this.logoImgUrlUpdate.emit(this.logoImgUrl);
          } else {
            let customLogo = CUSTOM_URLS.find(url => window.location.href.includes(url.urlText));
            this.logoImgUrl = customLogo?.img ? customLogo?.img : "assets/img/logos/arsondata.png";
            this.logoImgUrlUpdate.emit(this.logoImgUrl);
          }
        }
      });
    }
  }

  // Get client list array
  getClientList(): void {
    this.clientListOriginal = this.userData.map((client: Client) => client);
    this.sortBy(this.clientListOriginal, "clientName");
    this.clientList = [...this.clientListOriginal];
  }

  // Get entity list array
  getEntityList(): void {
    this.entityListOriginal = this.currentClient.entityList.map((entity: Entity) => entity);
    this.sortBy(this.entityListOriginal, "entity");
    this.entityList = [...this.entityListOriginal];
    this.SessionDataService.sendEntityList(this.entityListOriginal);
  }

  // Get agrupation list array
  getAgrupationList(): void {
    this.agrupationListOriginal =
      this.sessionProfile === PROFILES.ARSON || this.sessionProfile === PROFILES.USUARIO_ARSON
        ? this.currentEntity.agrupations.map((agrupation: Agrupation) => agrupation)
        : this.currentEntity.agrupations.filter(
            (agrupation: Agrupation) => !agrupation.hideInVirtualMap
          );
    this.sortBy(this.agrupationListOriginal, "name");

    let position = 0;

    let showAllEntity = this.agrupationListOriginal.findIndex(
      (agrupation: Agrupation) => agrupation.showAllEntity
    );

    if (showAllEntity >= 0) {
      this.changePosition(this.agrupationListOriginal, showAllEntity, position);
      position++;
    }

    let virtuals = this.agrupationListOriginal
      .map((agrupation: Agrupation, index: number) =>
        agrupation.virtual && !agrupation.showAllEntity ? index : -1
      )
      .filter(index => index !== -1);

    if (virtuals.length > 0) {
      virtuals.forEach((index, i) => {
        this.changePosition(this.agrupationListOriginal, index, i + position);
      });
    }

    this.agrupationList = [...this.agrupationListOriginal];
  }

  // Sort array of objects by attribute
  sortBy(data: any, attribute: string): void {
    data.sort((a: any, b: any) => {
      const valA = a[attribute] || "";
      const valB = b[attribute] || "";
      return valA.localeCompare(valB);
    });
  }

  // Reorder array
  changePosition(array: any[], index: number, newIndex: number): void {
    let element = array[index];
    array.splice(index, 1);
    array.splice(newIndex, 0, element);
  }

  // Update current client when selected in dropdown
  updateCurrentClient(selectedClient: Client): void {
    this.SessionDataService.sendClient(selectedClient);
  }

  // Update current entity when selected in dropdown
  updateCurrentEntity(selectedEntity: Entity): void {
    this.SessionDataService.sendEntity(selectedEntity);
  }

  // Update current agrupation when selected in dropdown
  updateCurrentAgrupation(selectedAgrupation: Agrupation): void {
    this.SessionDataService.sendAgrupation(selectedAgrupation);
  }

  /***************************************************************************/
  // ANCHOR Client, entity or agrupation change
  /***************************************************************************/

  checkEntityFlags(): void {
    this.SessionDataService.sendGasDay(this.currentEntity.gasClient);
    this.SessionDataService.sendGlobalManagement(this.currentEntity.globalManagement);
    this.SessionDataService.sendSharedGateways(this.currentEntity.sharedGateways);
  }

  checkClientFlags(): void {
    this.SessionDataService.sendGasDay(this.currentClient.gas);
    this.SessionDataService.sendGlobalManagement(this.currentClient.globalManagement);
    this.SessionDataService.sendSharedGateways(this.currentClient.sharedGateways);
  }

  // Select current client
  setCurrentClient(client: Client): void {
    if (client) {
      this.currentClient = client;
      this.currentClient.itemsCount = this.clientListOriginal.length || 1;
      this.getEntityList();
      const selectedEntity = untracked(() => this.SessionDataService.entity());
      if (
        !this.entityListOriginal.some(entity => entity.id === selectedEntity?.id) &&
        !untracked(() => this.SessionDataService.chatbotApplyingMatch())
      ) {
        this.SessionDataService.sendEntity(this.entityListOriginal[0]);
      }
      if (
        this.sessionProfile === PROFILES.ARSON ||
        this.sessionProfile === PROFILES.USUARIO_ARSON
      ) {
        this.checkClientFlags();
      }
    } else {
      this.currentClient = null;
      this.currentEntity = null;
      this.currentAgrupation = null;
      this.entityListOriginal = null;
      this.entityList = null;
      this.SessionDataService.clearEntity();
      this.SessionDataService.clearEntityList();
      this.SessionDataService.clearEntityExternalConfs();
      this.agrupationListOriginal = null;
      this.agrupationList = null;
      this.SessionDataService.clearAgrupation();
      setTimeout(() => this.router.navigate(["/clientes/formulario/nuevo"]), 0);
      this.ToastService.fireToast("warning", this.translate.instant("must-client"));
    }
  }

  // Select current entity
  setCurrentEntity(entity: Entity): void {
    this.navbarTopSearchBox?.resetSearchBoxText();
    this.navbarResponsive?.resetSearchDevice();
    if (entity) {
      this.addEntityCustomAlarmTranslations(entity.id);
      this.getEntityExternalConf(entity.id);
      if (this.sessionProfile !== PROFILES.TELEMEDIDA) {
        this.getEntityCupsConfiguration(entity);
      }
      this.currentEntity = entity;
      this.currentEntity.itemsCount = this.entityListOriginal.length || 1;
      this.getEntityImg();
      setTimeout(() => this.getSearchDropdown(), 0);
      if (this.sessionProfile != PROFILES.ARSON && this.sessionProfile != PROFILES.USUARIO_ARSON)
        this.checkEntityFlags();

      this.getAgrupationList();
      // Reset to the first non-virtual agrupation when the current selection belongs to another entity, unless a chatbot match is being applied.
      const selectedAgrupation = untracked(() => this.SessionDataService.agrupation());
      if (
        selectedAgrupation?.entity !== entity.id &&
        !untracked(() => this.SessionDataService.chatbotApplyingMatch())
      ) {
        this.SessionDataService.sendAgrupation(
          this.agrupationListOriginal.filter((agrupation: Agrupation) => !agrupation.virtual)[0]
        );
      }
      if (!this.uniqueClient && this.currentClient.clientId !== -1) {
        this.checkEntityClient();
      }
    } else {
      this.currentEntity = null;
      this.currentAgrupation = null;
      this.agrupationListOriginal = null;
      this.agrupationList = null;
      this.SessionDataService.clearAgrupation();
      this.SessionDataService.clearEntityExternalConfs();
      setTimeout(() => this.router.navigate(["/entidades/formulario/nuevo"]), 0);
      this.ToastService.fireToast("warning", this.translate.instant("must-entity"));
    }
  }

  // Load external configuration types for current entity
  getEntityExternalConf(entityId: number): void {
    if (!entityId) {
      this.SessionDataService.clearEntityExternalConfs();
      return;
    }
    this.ExternalConfigurationDeviceService.getEntityExternalConf(entityId).subscribe(response => {
      if (response["code"] === 0) {
        this.SessionDataService.sendEntityExternalConfs(response["body"]);
      } else {
        this.SessionDataService.clearEntityExternalConfs();
      }
    });
  }

  // Load dynamic custom alarm translations for all supported languages
  addEntityCustomAlarmTranslations(entityId: number): void {
    if (!entityId) {
      return;
    }
    this.SessionDataService.sendEntityCustomAlarmCodes(new Set());
    this.AlarmController.getEntityConsumptionsCustomAlarms(entityId).subscribe(response => {
      if (response["code"] !== 0) {
        return;
      }

      const responseBody = response["body"];
      const alarms: ConsumptionsCustomAlarmConfig[] = Array.isArray(responseBody)
        ? responseBody
        : responseBody?.alarms || [];

      const alertMeterTranslations: Record<string, string> = {};
      const alertDescriptionTranslations: Record<string, string> = {};
      const customAlarmCodes = new Set<number>();

      alarms.forEach((alarm: ConsumptionsCustomAlarmConfig) => {
        if (alarm?.code == null) {
          return;
        }
        const alarmCode = String(alarm.code);
        customAlarmCodes.add(alarm.code);
        if (alarm.name) {
          alertMeterTranslations[alarmCode] = alarm.name;
        }
        if (alarm.description) {
          alertDescriptionTranslations[alarmCode] = alarm.description;
        }
      });

      this.SessionDataService.sendEntityCustomAlarmCodes(customAlarmCodes);

      if (
        Object.keys(alertMeterTranslations).length === 0 &&
        Object.keys(alertDescriptionTranslations).length === 0
      ) {
        return;
      }

      const dynamicTranslations: Record<string, Record<string, string>> = {};
      if (Object.keys(alertMeterTranslations).length > 0) {
        dynamicTranslations["_alert_meter"] = alertMeterTranslations;
      }
      if (Object.keys(alertDescriptionTranslations).length > 0) {
        dynamicTranslations["_alert_description"] = alertDescriptionTranslations;
      }

      Object.values(LANGUAGE).forEach((language: string) => {
        this.translate.setTranslation(language, dynamicTranslations, true);
      });
    });
  }

  // Select current agrupation
  setCurrentAgrupation(agrupation: Agrupation): void {
    if (agrupation) {
      this.currentAgrupation = agrupation;
      this.currentAgrupation.itemsCount = this.agrupationListOriginal.length || 1;
      this.getClock();
    } else {
      this.currentAgrupation = null;
      if (this.currentEntity) {
        setTimeout(() => this.router.navigate(["/agrupaciones/formulario/nuevo"]), 0);
        this.ToastService.fireToast("warning", this.translate.instant("must-agrupation"));
      }
    }
  }

  // Get CUPS configuration for current entity
  getEntityCupsConfiguration(entity: Entity): void {
    if (entity?.id) {
      this.AssociationController.listEntityConfig(entity.id).subscribe(response => {
        if (response["code"] === 0) {
          this.currentEntityCupsConf = response["body"];
          this.currentCupsName = this.currentEntityCupsConf?.find(
            (column: EntityDefinition) => column.colPosition === 0
          )?.name;
          this.SessionDataService.sendEntityCupsConf(this.currentEntityCupsConf);
          this.SessionDataService.sendEntityCupsConfParsed(this.getEntityCupsData());
        }
      });
    } else {
      this.SessionDataService.clearEntityCupsConf();
    }
  }

  // Get entity CUPS data
  getEntityCupsData(): {
    entityCups: EntityDefinition;
    entityNroSerie: EntityDefinition;
    entityPta: EntityDefinition;
    entityExtraData: EntityDefinition[];
    entityCupsColumns: TableDataColumn[];
  } {
    const entityCupsDefinitions = structuredClone(
      untracked(() => this.SessionDataService.entityCupsConf())
    );
    if (!entityCupsDefinitions || entityCupsDefinitions.length === 0) {
      return {
        entityCups: null,
        entityNroSerie: null,
        entityPta: null,
        entityExtraData: [],
        entityCupsColumns: []
      };
    }
    const cupsColums = [];
    const entityExtraData = [];
    const entityCups: EntityDefinition = entityCupsDefinitions?.find(
      (column: EntityDefinition) => column.colPosition === CUPS_COLUMNS_INDEX.CUPS
    );
    const entityNroSerie: EntityDefinition = entityCupsDefinitions?.find(
      (column: EntityDefinition) => column.colPosition === CUPS_COLUMNS_INDEX.NRO_SERIE
    );
    const entityPta: EntityDefinition = entityCupsDefinitions?.find(
      (column: EntityDefinition) => column.colPosition === CUPS_COLUMNS_INDEX.PTA
    );
    entityCupsDefinitions?.forEach((cupsDefinition: EntityDefinition) => {
      if (
        cupsDefinition.colPosition !== CUPS_COLUMNS_INDEX.CUPS &&
        cupsDefinition.colPosition !== CUPS_COLUMNS_INDEX.NRO_SERIE &&
        cupsDefinition.colPosition !== CUPS_COLUMNS_INDEX.PTA
      ) {
        const col =
          cupsDefinition.colPosition <= 9
            ? "col0" + cupsDefinition.colPosition
            : "col" + cupsDefinition.colPosition;
        let newColumn: TableDataColumn = {
          title: cupsDefinition.name,
          data: col,
          search: col,
          sort: col,
          long: true,
          visible: cupsDefinition.show ? true : null
        };
        cupsColums.push(newColumn);
        entityExtraData.push(cupsDefinition);
      }
    });
    return {
      entityCups: entityCups,
      entityNroSerie: entityNroSerie,
      entityPta: entityPta,
      entityExtraData: entityExtraData,
      entityCupsColumns: cupsColums
    };
  }

  /***************************************************************************/
  // ANCHOR Device and CUPS search
  /***************************************************************************/

  // Get search lists
  getSearchDropdown(): void {
    this.meterList = null;
    this.cupsList = null;
    if (
      this.sessionProfile !== PROFILES.ABONADO &&
      this.sessionProfile !== PROFILES.INSTALADOR &&
      this.sessionProfile !== PROFILES.ADMIN_INSTALLATION &&
      this.sessionProfile !== PROFILES.TELEMEDIDA
    ) {
      this.getMetersCupsDropdown();
    } else if (
      this.sessionProfile !== PROFILES.INSTALADOR &&
      this.sessionProfile !== PROFILES.ADMIN_INSTALLATION &&
      this.sessionProfile !== PROFILES.TELEMEDIDA
    ) {
      this.getOnlyCupsDropdown();
    } else {
      // Nothing to do
    }
  }

  // Get meter and CUPS list
  getMetersCupsDropdown(): void {
    this.DataAnalysisController.getMetersDropdown(this.currentEntity.id).subscribe(response => {
      if (response["code"] === 0) {
        this.searchLabel = response["body"]["fieldName"];
        this.meterList = response["body"]["nroSerieList"].map((meter: any) => {
          return {
            descripcion: meter.nroSerie,
            id: meter.id,
            agrupation: meter.agrupation,
            type: meter.metrologyType
          };
        });
        this.cupsList = response["body"]["dropdownList"].map((cups: any) => {
          return {
            descripcion: cups.clave,
            id: cups.id,
            entity: cups.entity
          };
        });
        this.sortBy(this.meterList, "descripcion");
        this.sortBy(this.cupsList, "descripcion");
        this.meterListOriginal = [...this.meterList];
        this.cupsListOriginal = [...this.cupsList];
        this.SessionDataService.sendCupsList(this.cupsList);
      }
    });
  }

  // Get CUPS list only
  getOnlyCupsDropdown(): void {
    this.DataAnalysisController.getCupsDropdown().subscribe(response => {
      if (response["code"] === 0) {
        this.searchLabel = response["body"]["fieldName"];
        this.cupsList = response["body"]["dropdownList"].map((cups: any) => {
          return {
            descripcion: cups.clave,
            id: cups.id
          };
        });
        this.sortBy(this.cupsList, "descripcion");
        this.cupsListOriginal = [...this.cupsList];
        this.SessionDataService.sendCupsList(this.cupsList);
      }
    });
  }

  goToCupsDetail(selection: any): void {
    const entity = this.entityListOriginal.find((entity: any) => entity.id === selection.entity);
    if (entity?.id !== this.currentEntity?.id) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      this.SessionDataService.sendEntity(entity);
    }
    this.router.navigate(["/cups/detalle/", selection.id]);
  }

  goToDevice(selection: any): void {
    const agrupation = this.agrupationListOriginal.find(
      (agrupation: any) => agrupation.id === selection.agrupation
    );
    if (agrupation?.id !== this.currentAgrupation?.id) {
      this.SessionDataService.sendAvoidNavigationReload(true);
      this.SessionDataService.sendAgrupation(agrupation);
    }
    this.DeviceRouteSelectorService.getDeviceRoute(selection.type, selection.id);
  }

  // Switch the current entity to the one that owns the agrupation, when they differ
  checkAgrupationEntity(agrupation: Agrupation): void {
    if (this.currentEntity && agrupation?.entity && agrupation.entity !== this.currentEntity.id) {
      const foundEntity: Entity =
        this.sessionProfile === PROFILES.ARSON || this.sessionProfile === PROFILES.USUARIO_ARSON
          ? this.NavigationHelperService.findEntityInAllClients("id", agrupation.entity)?.entity
          : this.NavigationHelperService.findEntityInCurrentClient("id", agrupation.entity);
      this.SessionDataService.sendEntity(foundEntity);
    }
  }

  // Switch the current client to the one that owns the entity; falls back to a placeholder client when it is not in the list
  checkEntityClient(): void {
    if (this.currentEntity.client !== this.currentClient.clientId) {
      let foundClient: any = this.clientListOriginal.find(
        client => client.clientId === this.currentEntity.client
      );
      if (foundClient) {
        this.SessionDataService.sendClient(foundClient);
      } else {
        this.SessionDataService.sendClient({
          clientId: -1,
          clientName: "-",
          entityList: [this.currentEntity]
        });
      }
    }
  }

  /***************************************************************************/
  // ANCHOR Clock and time
  /***************************************************************************/

  // Initialize clock
  getClock(): void {
    if (this.currentAgrupation?.timezone) {
      this.clockTime.offset = this.DateParserService.getTimezoneOffset(
        this.currentAgrupation?.timezone
      );
      this.clockTimer = setInterval(() => {
        if (this.currentAgrupation && this.currentAgrupation.name !== "-") {
          let format: string = "DD/MM - HH:mm:ss";
          let clockTime: any = this.DateParserService.getClock(format);
          this.clockTime.date = clockTime?.substring(0, clockTime?.indexOf("-") - 1);
          this.clockTime.time = clockTime?.substring(clockTime?.indexOf("-") + 1);
        } else {
          this.clockTime.date = null;
          this.clockTime.time = null;
          clearInterval(this.clockTimer);
        }
      }, 1000);
    }
  }

  // Update weather data
  updateWeatherData(): void {
    this.weatherLoading = true;
    this.weatherUpdateEnabled = false;
    let lang: string = this.sessionLanguage === LANGUAGE.CATALAN ? "ca" : this.sessionLanguage;

    if (
      this.currentWeatherCoords?.latitude != null &&
      this.currentWeatherCoords?.longitude != null
    ) {
      this.WeatherApiController.getWeatherData(
        this.currentWeatherCoords.latitude,
        this.currentWeatherCoords.longitude,
        lang
      ).subscribe({
        next: response => {
          // code != 0: HttpRequestService already shows the error toast
          if (response["code"] !== 0) {
            this.weatherLoading = false;
            return;
          }
          const weather = response["body"];
          if (weather?.["weather"]?.[0]) {
            this.weatherData = {
              text: weather["weather"][0]["description"],
              icon: this.weatherTypes[weather["weather"][0]["main"].toLowerCase()],
              temp: weather["main"]["temp"],
              maxTemp: weather["main"]["temp_max"],
              minTemp: weather["main"]["temp_min"],
              pressure: weather["main"]["pressure"],
              humidity: weather["main"]["humidity"],
              sunrise: this.DateParserService.parseDate(weather["sys"]["sunrise"] * 1000, "HH:mm"),
              sunset: this.DateParserService.parseDate(weather["sys"]["sunset"] * 1000, "HH:mm"),
              clouds: weather["clouds"]["all"]
            };
          } else {
            this.ToastService.fireToast("error", this.translate.instant("weather-load-error"));
          }
          this.weatherLoading = false;
        },
        error: () => {
          this.ToastService.fireToast("error", this.translate.instant("weather-load-error"));
          this.weatherLoading = false;
        }
      });
    } else {
      this.weatherLoading = false;
      this.ToastService.fireToast("warning", this.translate.instant("weather-data-error"));
    }
  }

  /***************************************************************************/
  // ANCHOR User settings and notifications
  /***************************************************************************/

  // Delete local configuration
  deleteUserLocalSettings(): void {
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");
    if (configData) {
      let userConfigIndex: number = configData.findIndex(
        (config: UserConfig) => config.user === untracked(() => this.SessionDataService.user())
      );
      configData.splice(userConfigIndex, 1);
    }
    this.BrowserStorageLocalService.setJsonValue("config", configData);
    this.ToastService.fireToast(
      "success",
      this.translate.instant("user-local-configuration-deleted")
    );
    window.location.reload();
  }

  // Reset map settings to defaults
  resetMapSettings(): void {
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");
    if (configData) {
      const userConfig = configData.find(
        (config: UserConfig) => config.user === untracked(() => this.SessionDataService.user())
      );
      if (userConfig) {
        userConfig.mapConf = {};
        this.BrowserStorageLocalService.setJsonValue("config", configData);
      }
    }
    this.ToastService.fireToast("success", this.translate.instant("reset-map-settings-done"));
    this.SessionDataService.sendResetMapConfigFlag();
  }

  // Get pending notification count
  getPendingNotifications() {
    this.NotificationController.getUserPendingNotifications().subscribe(response => {
      if (response["code"] === 0) {
        this.pendingNotifications = response["body"];
        if (this.pendingNotifications > 0) {
          this.ToastService.fireToastWithConfirmation(
            "info",
            this.translate.instant("notifications-new"),
            this.translate.instant("show"),
            this.translate.instant("close")
          ).then((userConfirmation: boolean) => {
            if (userConfirmation) {
              this.NotificationModalService.showNotifications();
            }
          });
        }
      }
    });
  }

  // Update user restrictions
  updateUserRestrictions(userRestrictionsActive: boolean): void {
    this.UserController.updateRestrictions(userRestrictionsActive).subscribe(response => {
      if (response["code"] === 0) {
        this.SessionDataService.restrictionsActive.set(userRestrictionsActive);
        this.ToastService.fireToast(
          "success",
          this.translate.instant(
            userRestrictionsActive
              ? "user-restrictions-activated"
              : "user-restrictions-disabled"
          )
        );
      }
    });
  }

  // Update suspicion display
  updateUserSuspicion(userSuspicionActive: boolean): void {
    this.SessionDataService.sendSuspicionActive(userSuspicionActive);
    this.SessionDataService.sendReloadPanelFlag();
    this.ToastService.fireToast(
      "success",
      this.translate.instant(userSuspicionActive ? "suspicions-enabled" : "suspicions-disabled")
    );
  }

  updateUserDataProtection(dataProtectionActive: boolean): void {
    this.ToastService.fireToast(
      "success",
      this.translate.instant(
        dataProtectionActive ? "data-protection-enabled" : "data-protection-disabled"
      )
    );
  }

  // Update main page
  updateUserMainPage(mainPage: string): void {
    let userConfig: UserConfig;
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");

    if (configData?.length > 0) {
      userConfig = configData.find(
        (config: UserConfig) => config.user === untracked(() => this.SessionDataService.user())
      );
    }
    if (userConfig) {
      userConfig.mainPage = mainPage;
    } else {
      let userConfig = new UserConfig(untracked(() => this.SessionDataService.user()));
      userConfig.mainPage = mainPage;
      configData = [userConfig];
    }
    this.BrowserStorageLocalService.setJsonValue("config", configData);
    this.SessionDataService.sendMainPageUpdatedFlag(mainPage);
    this.ToastService.fireToast("success", this.translate.instant("saved"));
  }
}
